import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { Alert } from 'components/FbUI/Alert'
import { FbModal as Modal } from '../../components/UI/Modal'
import ReactSelectWrapper from '../../components/FormUtils/ReactSelectWrapper'
import { getApiErrorDetail } from '../../utils/getApiErrorDetail'
import { useAuth } from '../../context/authentication/useAuth'
import { useGetCampaignSenderEmails } from './sender_emails_api'
import { useSendStepTestEmail } from './sequences_api'

interface Props {
  show: boolean
  onHide: () => void
  bisonStepId: number | null
  bisonCampaignId: number
  stepNumber: number
  /**
   * Display label for the message under test, e.g. "Step 1 · Variant B". When
   * omitted, falls back to "Step {stepNumber}". Variants have no meaningful
   * sequential number, so callers pass the family-aware label here.
   */
  stepLabel?: string
  unresolvedVariables: string[]
  /**
   * Optional per-contact preview. When supplied, the test send uses these
   * already-substituted values instead of letting EmailBison fill variables
   * from a random campaign lead, so the recipient sees exactly what this
   * contact would receive.
   */
  renderedFor?: {
    subject: string
    body: string
    plainText: boolean
    contactName: string
  } | null
}

/**
 * Modal for sending a sequence-step test email through EmailBison.
 *
 * Two modes:
 * - Default (random contact): backend hits Bison's `/test-email` endpoint and
 *   Bison fills variables from a random campaign lead.
 * - Per-contact (when `renderedFor` is set): caller pre-substitutes variables
 *   for a specific contact and the backend routes through Bison's compose
 *   endpoint so the recipient sees exactly that contact's rendered email.
 */
export function SendStepTestEmailModal({
  show,
  onHide,
  bisonStepId,
  bisonCampaignId,
  stepNumber,
  stepLabel,
  unresolvedVariables,
  renderedFor,
}: Props) {
  const { user } = useAuth()
  const { data: sendersResponse, isLoading: isSendersLoading } =
    useGetCampaignSenderEmails(bisonCampaignId)
  const senders = useMemo(
    () => sendersResponse?.results ?? [],
    [sendersResponse]
  )
  const senderOptions = useMemo(
    () =>
      senders.map((sender) => ({
        value: sender.id,
        label: sender.name ? `${sender.name} <${sender.email}>` : sender.email,
      })),
    [senders]
  )

  // Fall back to the first sender as it loads async, without clobbering an
  // explicit pick — avoids the prop→state effect this would otherwise require.
  const [pickedSenderId, setPickedSenderId] = useState<number | null>(null)
  const senderId = pickedSenderId ?? senders[0]?.id ?? null
  const selectedSenderOption =
    senderOptions.find((option) => option.value === senderId) ?? null
  const [toEmail, setToEmail] = useState<string>(user?.email ?? '')

  const sendMutation = useSendStepTestEmail()

  const hasVariableWarnings = unresolvedVariables.length > 0
  const hasSender = senderId != null
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim())
  const canSubmit =
    !hasVariableWarnings &&
    bisonStepId != null &&
    hasSender &&
    validEmail &&
    !sendMutation.isPending

  async function handleSubmit() {
    if (!canSubmit || bisonStepId == null || senderId == null) return
    try {
      await sendMutation.mutateAsync({
        bisonStepId,
        payload: {
          sender_email_id: senderId,
          to_email: toEmail.trim(),
          ...(renderedFor
            ? {
                rendered_subject: renderedFor.subject,
                rendered_body: renderedFor.body,
                plain_text: renderedFor.plainText,
                recipient_name: renderedFor.contactName,
              }
            : {}),
        },
      })
      toast.success(`Test email sent to ${toEmail.trim()}`)
      onHide()
    } catch (err: unknown) {
      toast.error(getApiErrorDetail(err, 'Failed to send test email'))
    }
  }

  const noSenders = !isSendersLoading && senders.length === 0

  const description = renderedFor
    ? `Sends the email exactly as ${renderedFor.contactName || 'this contact'} would receive it. Variables are already substituted from their contact details.`
    : 'Sends a real email through the same pipeline as your campaign so formatting and spacing match what contacts will receive. Custom variables are filled from a random campaign contact.'

  return (
    <Modal
      open={show}
      onOpenChange={(open: boolean) => !open && onHide()}
      title={`Send test email for ${stepLabel ?? `Step ${stepNumber}`}`}
      description={description}
      onAccept={handleSubmit}
      acceptButtonText="Send test email"
      blockAccept={!canSubmit}
      loading={sendMutation.isPending}
    >
      <div className="space-y-3">
        {bisonStepId == null && (
          <Alert
            color="warning"
            description="Save the sequence first. Test sends use the saved step content."
          />
        )}
        {hasVariableWarnings && (
          <Alert
            color="warning"
            description={
              <>
                <p>
                  Resolve unfilled variables before sending. Otherwise the test
                  would go out with empty placeholders.
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {unresolvedVariables.map((variable) => (
                    <code
                      key={variable}
                      className="rounded bg-warning-bg px-1.5 py-0.5 text-xxs text-fg-secondary"
                    >
                      {variable}
                    </code>
                  ))}
                </div>
              </>
            }
          />
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-fg">
            Send from
          </label>
          {isSendersLoading ? (
            <div className="rounded-lg border border-line bg-surface-alt px-3 py-2 text-xs text-fg-secondary">
              Loading senders…
            </div>
          ) : noSenders ? (
            <Alert
              color="warning"
              description="Attach a sender email to this campaign in the Settings step before sending a test."
            />
          ) : (
            <ReactSelectWrapper
              inputId="test-email-sender"
              instanceId="test-email-sender"
              menuPosition="fixed"
              isClearable={false}
              isSearchable={false}
              options={senderOptions}
              value={selectedSenderOption}
              onChange={(newValue: any) =>
                setPickedSenderId(newValue?.value ?? null)
              }
            />
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-fg">
            Send to
          </label>
          <input
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="e.g. you@firstbite.io"
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-brand"
          />
          {toEmail.trim().length > 0 && !validEmail && (
            <p className="mt-1 text-xs text-error">Enter a valid email.</p>
          )}
        </div>
      </div>
    </Modal>
  )
}
